import { useEffect } from "react";

let localDirtyCount = 0;
let pendingSaveCount = 0;
let listening = false;

const handleBeforeUnload = (event: BeforeUnloadEvent) => {
  event.preventDefault();
  // Chrome / older browsers require returnValue to trigger the dialog
  event.returnValue = "";
};

function syncBeforeUnloadListener() {
  const shouldWarn = localDirtyCount > 0 || pendingSaveCount > 0;
  if (shouldWarn && !listening) {
    window.addEventListener("beforeunload", handleBeforeUnload);
    listening = true;
  } else if (!shouldWarn && listening) {
    window.removeEventListener("beforeunload", handleBeforeUnload);
    listening = false;
  }
}

/**
 * Call when a debounced (or otherwise delayed) save is scheduled.
 * Invoke the returned function when the save finishes, or on unmount if abandoned.
 * Safe to keep one token across debounce restarts (clearTimeout + reschedule).
 */
export function beginPendingSave(): () => void {
  pendingSaveCount += 1;
  syncBeforeUnloadListener();

  let settled = false;
  return () => {
    if (settled) return;
    settled = true;
    pendingSaveCount = Math.max(0, pendingSaveCount - 1);
    syncBeforeUnloadListener();
  };
}

/**
 * Shows the browser's native "Leave site?" dialog while `when` is true
 * (e.g. local edits not yet flushed) or while any beginPendingSave() is outstanding.
 * Browsers control the dialog text — custom messages are ignored.
 */
export function useUnsavedChangesWarning(when: boolean): void {
  useEffect(() => {
    if (!when) return;

    localDirtyCount += 1;
    syncBeforeUnloadListener();

    return () => {
      localDirtyCount = Math.max(0, localDirtyCount - 1);
      syncBeforeUnloadListener();
    };
  }, [when]);
}
